import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Building2, Eye, LogOut } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/useToast'
import { describeError, errorMessage } from '@/lib/apiError'
import { Button } from '@/components/common/Button'
import { Modal } from '@/components/common/Modal'
import { Field, Select, Textarea } from '@/components/common/Input'
import { PasswordInput } from '@/components/common/PasswordInput'
import { endSupportAccess, getSupportAccess, listOrganisationsForSupport, startSupportAccess } from '@/api/supportAccess'

/**
 * "Act as organisation" (platform support access): SUPER_ADMIN only. Starting needs an
 * organisation, a reason the organisation will see in its audit trail, the operator's password
 * (step-up) and a duration of at most 60 minutes. The mode is read-only and every request is
 * audited in that organisation. While it is on, a persistent banner names the organisation and
 * offers Exit.
 */
const KEY = ['me', 'act-as'] as const

function useSupportAccess() {
  const { user } = useAuth()
  const isSuper = user?.role === 'SUPER_ADMIN'
  return useQuery({
    queryKey: KEY,
    queryFn: getSupportAccess,
    enabled: isSuper,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    meta: { inlineError: true },
  })
}

/** Leaves the mode: every cached page belonged to the other organisation, so drop them all. */
function useExit() {
  const qc = useQueryClient()
  const nav = useNavigate()
  const toast = useToast()
  return useMutation({
    mutationFn: endSupportAccess,
    onSuccess: () => {
      qc.clear()
      toast.success('Support access ended. You are back in your own organisation.')
      nav('/admin/organizations')
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
}

export function SupportAccessBanner() {
  const q = useSupportAccess()
  const exit = useExit()
  const s = q.data
  if (!s?.active) return null
  const until = s.expires_at ? new Date(s.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
  return (
    <div role="status" data-testid="support-access-banner" className="flex flex-wrap items-center gap-2 border-b border-warning/30 bg-warning-subtle px-4 py-2 text-sm text-warning-content print:hidden">
      <Eye className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="font-semibold">Acting as {s.organization?.name ?? 'another organisation'}</span>
        {' '}— read-only support access until {until}. Every page you open is recorded in their audit trail.
      </span>
      <Button size="sm" variant="outline" icon={<LogOut />} loading={exit.isPending} onClick={() => exit.mutate()}>Exit</Button>
    </div>
  )
}

/** Topbar entry point (SUPER_ADMIN only): pick an organisation and start the mode. */
export function ActAsOrganisationButton() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const q = useSupportAccess()
  if (user?.role !== 'SUPER_ADMIN' || q.data?.active) return null
  return (
    <>
      <Button size="sm" variant="ghost" icon={<Building2 />} onClick={() => setOpen(true)} title="Look into a client organisation (read-only, audited)">
        <span className="hidden lg:inline">Act as organisation</span>
      </Button>
      {open && <ActAsDialog ownTenant={user.tenant_id ?? null} onClose={() => setOpen(false)} />}
    </>
  )
}

function ActAsDialog({ ownTenant, onClose }: { ownTenant: string | null; onClose: () => void }) {
  const qc = useQueryClient()
  const nav = useNavigate()
  const toast = useToast()
  const orgs = useQuery({ queryKey: ['admin', 'organizations', 'support-picker'], queryFn: listOrganisationsForSupport })
  const [org, setOrg] = useState('')
  const [reason, setReason] = useState('')
  const [password, setPassword] = useState('')
  const [ttl, setTtl] = useState(30)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const start = useMutation({
    mutationFn: () => startSupportAccess(org, { reason: reason.trim(), password, ttl_minutes: ttl }),
    onSuccess: (s) => {
      qc.clear()
      toast.success(`Acting as ${s.organization?.name ?? 'the organisation'} (read-only).`)
      onClose()
      nav('/dashboard')
    },
    onError: (e) => {
      const info = describeError(e)
      setErrors(info.fieldErrors)
      if (!Object.keys(info.fieldErrors).length) toast.error(errorMessage(e))
    },
  })

  const submit = () => {
    const errs: Record<string, string> = {}
    if (!org) errs.organization = 'Choose the organisation.'
    if (reason.trim().length < 10) errs.reason = 'Say why you need to look (at least 10 characters) — the organisation will see it.'
    if (!password) errs.password = 'Enter your password to confirm it is you.'
    setErrors(errs)
    if (!Object.keys(errs).length) start.mutate()
  }

  const choices = (orgs.data ?? []).filter((o) => o.id !== ownTenant)
  return (
    <Modal
      open
      onClose={onClose}
      title="Act as organisation"
      description="Read-only support access to one client organisation, for up to an hour. The organisation sees your name, the reason and every page you open in its audit trail."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={start.isPending} onClick={submit}>Start read-only access</Button>
      </>}
    >
      <div className="space-y-4">
        <Field label="Organisation" required error={errors.organization}>
          <Select value={org} onChange={(e) => setOrg(e.target.value)} disabled={orgs.isLoading}>
            <option value="">{orgs.isLoading ? 'Loading…' : 'Choose…'}</option>
            {choices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
        </Field>
        <Field label="Reason" required error={errors.reason} hint="For example, the support ticket you are working on.">
          <Textarea rows={3} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Field label="Duration" required>
          <Select value={String(ttl)} onChange={(e) => setTtl(Number(e.target.value))}>
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="60">60 minutes</option>
          </Select>
        </Field>
        <Field label="Your password" required error={errors.password}>
          <PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
